import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { apiClient } from "@/lib/api/client";
import type { ShipmentTemplatePayload, CreateTemplatePayload } from "@/lib/api/types";
import {
  ArrowRight,
  Download,
  Edit2,
  Plus,
  Star,
  StarOff,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

const FREIGHT_CLASS_OPTIONS = [
  "50", "55", "60", "65", "70", "77.5", "85", "92.5",
  "100", "125", "150", "175", "200", "250", "300", "400", "500",
];

const HANDLING_UNIT_OPTIONS = ["1", "2", "3", "4", "5", "6", "8", "10"];
const MAX_IMPORT_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
const MAX_IMPORT_TEMPLATES = 500;

type DialogMode = "create" | "edit";

type TemplateFormState = {
  name: string;
  mode: "ltl" | "parcel";
  originAddress: string;
  originCity: string;
  originState: string;
  originPostalCode: string;
  destAddress: string;
  destCity: string;
  destState: string;
  destPostalCode: string;
  weight: string;
  freightClass: string;
  handlingUnits: string;
  isFavorite: boolean;
};

type ImportValidationResult =
  | { ok: true; payload: { templates: CreateTemplatePayload[] } }
  | { ok: false; error: string };

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validateImportedTemplates(value: unknown): ImportValidationResult {
  const payload = Array.isArray(value) ? { templates: value } : value;
  if (!isRecord(payload) || !Array.isArray(payload.templates)) {
    return { ok: false, error: "Invalid import file: expected JSON object { templates: [...] }." };
  }

  if (payload.templates.length > MAX_IMPORT_TEMPLATES) {
    return { ok: false, error: `Import file exceeds limit (${MAX_IMPORT_TEMPLATES} templates max).` };
  }

  for (let index = 0; index < payload.templates.length; index += 1) {
    const candidate = payload.templates[index];
    if (!isRecord(candidate)) {
      return { ok: false, error: `Template #${index + 1} must be an object.` };
    }

    const name = candidate.name;
    if (typeof name !== "string" || name.trim().length === 0 || name.trim().length > 200) {
      return { ok: false, error: `Template #${index + 1} has an invalid name.` };
    }

    const mode = candidate.mode;
    if (mode !== undefined && mode !== "ltl" && mode !== "parcel") {
      return { ok: false, error: `Template #${index + 1} has an invalid mode.` };
    }

    const items = candidate.items;
    if (items !== undefined) {
      if (!Array.isArray(items)) {
        return { ok: false, error: `Template #${index + 1} items must be an array.` };
      }
      if (items.length > 50) {
        return { ok: false, error: `Template #${index + 1} has too many items (50 max).` };
      }
      for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
        const item = items[itemIndex];
        if (!isRecord(item)) {
          return { ok: false, error: `Template #${index + 1} item #${itemIndex + 1} is invalid.` };
        }

        const numericFields: Array<keyof typeof item> = ["handlingUnits", "weight", "freightClass", "pieces"];
        for (const field of numericFields) {
          const raw = item[field];
          if (raw !== undefined && !isFiniteNumber(raw)) {
            return {
              ok: false,
              error: `Template #${index + 1} item #${itemIndex + 1} has invalid ${String(field)}.`,
            };
          }
        }
      }
    }
  }

  return { ok: true, payload: { templates: payload.templates as CreateTemplatePayload[] } };
}

function emptyForm(): TemplateFormState {
  return {
    name: "",
    mode: "ltl",
    originAddress: "",
    originCity: "",
    originState: "",
    originPostalCode: "",
    destAddress: "",
    destCity: "",
    destState: "",
    destPostalCode: "",
    weight: "",
    freightClass: "50",
    handlingUnits: "1",
    isFavorite: false,
  };
}

function templateToForm(t: ShipmentTemplatePayload): TemplateFormState {
  const item = t.items?.[0];
  return {
    name: t.name,
    mode: t.mode,
    originAddress: t.origin_address,
    originCity: t.origin_city,
    originState: t.origin_state,
    originPostalCode: t.origin_postal_code,
    destAddress: t.dest_address,
    destCity: t.dest_city,
    destState: t.dest_state,
    destPostalCode: t.dest_postal_code,
    weight: String(item?.weight ?? ""),
    freightClass: String(item?.freightClass ?? "50"),
    handlingUnits: String(item?.handlingUnits ?? "1"),
    isFavorite: t.is_favorite,
  };
}

function formToPayload(form: TemplateFormState): CreateTemplatePayload {
  return {
    name: form.name,
    mode: form.mode,
    origin_address: form.originAddress,
    origin_city: form.originCity,
    origin_state: form.originState,
    origin_postal_code: form.originPostalCode,
    dest_address: form.destAddress,
    dest_city: form.destCity,
    dest_state: form.destState,
    dest_postal_code: form.destPostalCode,
    items: [
      {
        handlingUnits: Number(form.handlingUnits) || 1,
        weight: Number(form.weight) || 0,
        freightClass: Number(form.freightClass) || 50,
        pieces: Number(form.handlingUnits) || 1,
      },
    ],
    is_favorite: form.isFavorite,
  };
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [templates, setTemplates] = React.useState<ShipmentTemplatePayload[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<DialogMode>("create");
  const [dialogSaving, setDialogSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<TemplateFormState>(emptyForm());

  // Import
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTemplates();
      setTemplates(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load templates.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Keyboard handler for dialog
  React.useEffect(() => {
    if (!dialogOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDialogOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [dialogOpen]);

  const flashMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3000);
  };

  const openCreate = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (t: ShipmentTemplatePayload) => {
    setDialogMode("edit");
    setEditingId(t.id);
    setForm(templateToForm(t));
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (dialogMode === "edit" && editingId) {
        const updated = await apiClient.updateTemplate(editingId, payload);
        setTemplates((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
        flashMessage("Template updated.");
      } else {
        const created = await apiClient.createTemplate(payload);
        setTemplates((prev) => [...prev, created]);
        flashMessage("Template created.");
      }
      setDialogOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save template.");
    } finally {
      setDialogSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await apiClient.deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      flashMessage("Template deleted.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to delete template.");
    }
  };

  const handleToggleFavorite = async (id: string) => {
    setError(null);
    try {
      const updated = await apiClient.toggleTemplateFavorite(id);
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? updated : t)).sort((a, b) => {
          if (a.is_favorite !== b.is_favorite) return a.is_favorite ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to toggle favorite.");
    }
  };

  const handleExport = async () => {
    setError(null);
    try {
      const data = await apiClient.exportTemplates();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shippeek-templates-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      flashMessage("Templates exported.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to export templates.");
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
      setError(`Import file is too large. Maximum size is ${(MAX_IMPORT_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      const validation = validateImportedTemplates(parsed);
      if (!validation.ok) {
        setError(validation.error);
        return;
      }

      const result = await apiClient.importTemplates(validation.payload);
      flashMessage(`Imported ${result.imported} template(s).`);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to import templates.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const useForQuote = (t: ShipmentTemplatePayload) => {
    // Store template data so quotes page can pick it up
    sessionStorage.setItem("shippeek-load-template", JSON.stringify(t));
    navigate(t.mode === "parcel" ? "/quotes?mode=parcel" : "/quotes");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>Shipment Templates</CardTitle>
            <CardDescription>
              Save frequently-used origin/destination/item combinations for one-click quoting.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleExport} disabled={templates.length === 0}>
              <Download className="size-4" />
              Export
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="size-4" />
              Import
            </Button>
            <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImportFile} />
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="size-4" />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
          {message ? <p className="mb-4 text-sm text-emerald-600">{message}</p> : null}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" />
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground">
              <p>No templates yet.</p>
              <Button type="button" size="sm" onClick={openCreate}>
                <Plus className="size-4" />
                Create Your First Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  onEdit={() => openEdit(t)}
                  onDelete={() => handleDelete(t.id)}
                  onToggleFavorite={() => handleToggleFavorite(t.id)}
                  onUseForQuote={() => useForQuote(t)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      {dialogOpen ? (
        <TemplateDialog
          mode={dialogMode}
          form={form}
          setForm={setForm}
          saving={dialogSaving}
          onSubmit={handleSave}
          onClose={() => setDialogOpen(false)}
          error={error}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Card
// ---------------------------------------------------------------------------

function TemplateCard({
  template: t,
  onEdit,
  onDelete,
  onToggleFavorite,
  onUseForQuote,
}: {
  template: ShipmentTemplatePayload;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  onUseForQuote: () => void;
}) {
  const item = t.items?.[0];

  return (
    <div className="group relative rounded-lg border-[0.5px] border-border bg-card p-4 transition-colors hover:border-foreground/20">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold">{t.name}</h3>
            <Badge variant="outline" className="shrink-0 text-[0.65rem]">
              {t.mode.toUpperCase()}
            </Badge>
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-muted-foreground transition-colors hover:text-yellow-500"
          onClick={onToggleFavorite}
          aria-label={t.is_favorite ? "Remove from favorites" : "Add to favorites"}
        >
          {t.is_favorite ? (
            <Star className="size-4 fill-yellow-400 text-yellow-500" />
          ) : (
            <StarOff className="size-4" />
          )}
        </button>
      </div>

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">From:</span>{" "}
          {t.origin_city}, {t.origin_state} {t.origin_postal_code}
        </p>
        <p>
          <span className="font-medium text-foreground">To:</span>{" "}
          {t.dest_city}, {t.dest_state} {t.dest_postal_code}
        </p>
        {item ? (
          <p>
            <span className="font-medium text-foreground">Item:</span>{" "}
            {item.weight} lbs, Class {item.freightClass}, {item.handlingUnits} HU
          </p>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        <Button type="button" size="sm" variant="default" className="flex-1" onClick={onUseForQuote}>
          <ArrowRight className="size-3.5" />
          Quote
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onEdit} aria-label="Edit template">
          <Edit2 className="size-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onDelete} aria-label="Delete template">
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Dialog (create / edit overlay)
// ---------------------------------------------------------------------------

function TemplateDialog({
  mode,
  form,
  setForm,
  saving,
  onSubmit,
  onClose,
  error,
}: {
  mode: DialogMode;
  form: TemplateFormState;
  setForm: React.Dispatch<React.SetStateAction<TemplateFormState>>;
  saving: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  error: string | null;
}) {
  const update = <K extends keyof TemplateFormState>(key: K, value: TemplateFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={mode === "edit" ? "Edit Template" : "New Template"}>
      <div className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border-[0.5px] border-border bg-card">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-card px-6 py-4">
          <h2 className="text-lg font-semibold">
            {mode === "edit" ? "Edit Template" : "New Template"}
          </h2>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Close">
            <X className="size-5" />
          </button>
        </div>

        <form className="space-y-4 px-6 py-4" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Template Name</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Dallas to Austin Standard" required />
            </div>

            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => update("mode", v as "ltl" | "parcel")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ltl">LTL</SelectItem>
                  <SelectItem value="parcel">Parcel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isFavorite}
                  onChange={(e) => update("isFavorite", e.target.checked)}
                  className="size-4 rounded border-border"
                />
                Favorite
              </Label>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Origin</p>
              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input value={form.originAddress} onChange={(e) => update("originAddress", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input value={form.originCity} onChange={(e) => update("originCity", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Input value={form.originState} onChange={(e) => update("originState", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>ZIP</Label>
                  <Input value={form.originPostalCode} onChange={(e) => update("originPostalCode", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Destination</p>
              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input value={form.destAddress} onChange={(e) => update("destAddress", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input value={form.destCity} onChange={(e) => update("destCity", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Input value={form.destState} onChange={(e) => update("destState", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>ZIP</Label>
                  <Input value={form.destPostalCode} onChange={(e) => update("destPostalCode", e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Freight Details</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Weight (lbs)</Label>
              <Input value={form.weight} onChange={(e) => update("weight", e.target.value)} type="number" min="0" />
            </div>
            <div className="space-y-1.5">
              <Label>Freight Class</Label>
              <Select value={form.freightClass} onValueChange={(v) => update("freightClass", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREIGHT_CLASS_OPTIONS.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Handling Units</Label>
              <Select value={form.handlingUnits} onValueChange={(v) => update("handlingUnits", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HANDLING_UNIT_OPTIONS.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="text-primary-foreground" />
                  Saving...
                </>
              ) : mode === "edit" ? (
                "Update Template"
              ) : (
                "Create Template"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
